import { mutatePalletErps } from "@/api/palletErp/usePalletErpQuery";
import customAlert from "@/features/ui/alert/alert";
import {
  DefaultApiErpSyncDeleteErpSplitOutsourcingRecordPostRequest,
  DefaultApiErpSyncDeleteErpSplitWorkRecordPostRequest,
  DefaultApiErpSyncPushErpSplitOutsourcingRecordPostRequest,
  DefaultApiErpSyncPushErpSplitWorkRecordPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface UseErpOutsourcingMutationsProps {
  setLoading: (loading: boolean) => void;
  setSelectedRows: (selectedRows: Set<string>) => void;
  refetch: () => void;
}
export const useErpOutsourcingMutations = (params: UseErpOutsourcingMutationsProps) => {
  const { setLoading, setSelectedRows, refetch } = params;
  const { t } = useTranslation();

  const { mutate: postERP } = useMutation(
    (params: DefaultApiErpSyncPushErpSplitWorkRecordPostRequest) =>
      mutatePalletErps
        .postERP(params)
        .mutationFn(params as DefaultApiErpSyncPushErpSplitWorkRecordPostRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        if (res.data.success && res.data.success.length) {
          const workStr = res.data.success.join(", ");
          customAlert(
            t("performance.erpSuccessMessage", { workStr: workStr }),
            t("performance.erp"),
            "info",
          );
        }

        if (res.data.fail && res.data.fail.length) {
          const workStr = res.data.fail.join(", ");
          customAlert(
            t("performance.erpFailMessage", { workStr: workStr }),
            t("performance.erp"),
            "red",
          );
        }
        setLoading(false);
        setSelectedRows(new Set());
        refetch();
      },
      onError: (err) => {
        setLoading(false);
      },
    },
  );

  // erp 전송 취소
  const { mutate: cancelERP } = useMutation(
    (params: DefaultApiErpSyncDeleteErpSplitWorkRecordPostRequest) =>
      mutatePalletErps
        .cancelERP(params)
        .mutationFn(params as DefaultApiErpSyncDeleteErpSplitWorkRecordPostRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        if (res.data.success && res.data.success.length) {
          const workStr = res.data.success.join(", ");
          customAlert(
            t("performance.erpCancelSuccessMessage", { workStr: workStr }),
            t("performance.erpCancel"),
            "info",
          );
        }

        if (res.data.fail && res.data.fail.length) {
          const workStr = res.data.fail.join(", ");
          customAlert(
            t("performance.erpCancelFailMessage", { workStr: workStr }),
            t("performance.erpCancel"),
            "red",
          );
        }
        setLoading(false);
        setSelectedRows(new Set());
        refetch();
      },
      onError: (err) => {
        setLoading(false);
      },
    },
  );

  // 외주 erp 전송
  const { mutate: postOutsourcingERP } = useMutation(
    (params: DefaultApiErpSyncPushErpSplitOutsourcingRecordPostRequest) =>
      mutatePalletErps
        .postERPoutsourcing(params)
        .mutationFn(params as DefaultApiErpSyncPushErpSplitOutsourcingRecordPostRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        if (res.data.success && res.data.success.length) {
          const workStr = res.data.success.join(", ");
          customAlert(
            t("performance.erpSuccessMessage", { workStr: workStr }),
            t("performance.erp"),
            "info",
          );
        }

        if (res.data.fail && res.data.fail.length) {
          const workStr = res.data.fail.join(", ");
          customAlert(
            t("performance.erpFailMessage", { workStr: workStr }),
            t("performance.erp"),
            "red",
          );
        }
        Promise.resolve(refetch())
          .then(() => {
            setLoading(false);
            setSelectedRows(new Set());
          })
          .catch((error) => {
            console.error("Error refetching data:", error);
            setLoading(false);
          });
      },
      onError: (err) => {
        setLoading(false);
      },
    },
  );

  // 외주 erp 전송 취소
  const { mutate: cancelOutsourcingERP } = useMutation(
    (params: DefaultApiErpSyncDeleteErpSplitOutsourcingRecordPostRequest) =>
      mutatePalletErps
        .cancelERPoutsourcing(params)
        .mutationFn(params as DefaultApiErpSyncDeleteErpSplitOutsourcingRecordPostRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        if (res.data.success && res.data.success.length) {
          const workStr = res.data.success.join(", ");
          customAlert(
            t("performance.erpCancelSuccessMessage", { workStr: workStr }),
            t("performance.erpCancel"),
            "info",
          );
        }

        if (res.data.fail && res.data.fail.length) {
          const workStr = res.data.fail.join(", ");
          customAlert(
            t("performance.erpCancelFailMessage", { workStr: workStr }),
            t("performance.erpCancel"),
            "red",
          );
        }
        Promise.resolve(refetch())
          .then(() => {
            setLoading(false);
            setSelectedRows(new Set());
          })
          .catch((error) => {
            console.error("Error refetching data:", error);
            setLoading(false);
          });
      },
      onError: (err) => {
        setLoading(false);
      },
    },
  );

  return { postERP, cancelERP, postOutsourcingERP, cancelOutsourcingERP };
};
